"use client";

import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Logo } from "@/app/_components/icons";
import { Loader2, Mail, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  sendPasswordResetCode,
  verifyPasswordResetCode,
  resetPassword,
} from "../lib/actions";

type Step = "email" | "otp" | "password" | "done";

function OtpInput({
  value,
  onChange,
  length = 4,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const handleKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !refs.current[i]?.value && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handleInput = (i: number, val: string) => {
    const char = val.replace(/\D/g, "").slice(-1);
    const arr = value.padEnd(length, " ").split("");
    arr[i] = char || " ";
    const next = arr.join("").trimEnd();
    onChange(next);
    if (char && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    onChange(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={value[i] ?? ""}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          className={`w-14 h-14 text-center text-[22px] font-semibold border-2 rounded-xl outline-none transition-all duration-200 bg-background ${
            value[i] && value[i] !== " "
              ? "border-primary text-primary"
              : "border-border text-foreground"
          } focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50`}
        />
      ))}
    </div>
  );
}

export default function ForgotPasswordForm() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSendCode = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email.trim())) { setError("Please enter a valid email address."); return; }
    setError("");
    setLoading(true);
    try {
      await sendPasswordResetCode(email.trim().toLowerCase());
      setStep("otp");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.replace(/\s/g, "").length < 4) { setError("Please enter the 4-digit code."); return; }
    setError("");
    setLoading(true);
    try {
      const res = await verifyPasswordResetCode(email.trim().toLowerCase(), otp.trim());
      if (!res.success) { setError(res.error ?? "Invalid code."); return; }
      setStep("password");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setError("");
    setLoading(true);
    try {
      const res = await resetPassword(email.trim().toLowerCase(), otp.trim(), password);
      if (!res.success) { setError(res.error ?? "Failed to reset password."); return; }
      setStep("done");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    setError("");
    setOtp("");
    await sendPasswordResetCode(email.trim().toLowerCase());
    setResending(false);
    setResendCooldown(60);
  };

  const panelVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -16 },
  };

  return (
    <div className="flex items-center justify-center flex-1 px-6 py-16 bg-background min-h-screen">
      <div className="w-full max-w-[380px] flex flex-col gap-7">
        {/* Mobile logo */}
        <Link href="/" className="lg:hidden text-[28px] hover:opacity-80 transition-opacity">
          <Logo />
        </Link>

        <AnimatePresence mode="wait">
          {/* ── Step: Email ── */}
          {step === "email" && (
            <motion.div key="email" variants={panelVariants} initial="initial" animate="animate" exit="exit"
              transition={{ duration: 0.25 }} className="flex flex-col gap-6">
              <div>
                <h1 className="text-[28px] font-semibold tracking-tight leading-tight">Forgot password?</h1>
                <p className="text-muted-foreground mt-1.5 text-[15px]">
                  Enter your email and we&apos;ll send you a reset code.
                </p>
              </div>

              <div className="flex flex-col gap-3.5">
                <div className={`flex items-center gap-3 px-4 py-3 text-sm border rounded-xl bg-background transition-colors ${
                  error ? "border-red-400" : "border-border focus-within:border-primary"
                }`}>
                  <Mail size={15} className="text-muted-foreground flex-shrink-0" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
                    className="flex-1 bg-transparent outline-none"
                  />
                </div>

                {error && <p className="text-red-500 text-xs">{error}</p>}

                <button
                  onClick={handleSendCode}
                  disabled={loading}
                  className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send reset code"}
                </button>
              </div>

              <Link href="/login" className="text-center text-sm text-muted-foreground flex items-center justify-center gap-1.5 hover:text-foreground transition-colors">
                <ArrowLeft size={13} /> Back to sign in
              </Link>
            </motion.div>
          )}

          {/* ── Step: OTP ── */}
          {step === "otp" && (
            <motion.div key="otp" variants={panelVariants} initial="initial" animate="animate" exit="exit"
              transition={{ duration: 0.25 }} className="flex flex-col gap-6">
              <div>
                <h1 className="text-[28px] font-semibold tracking-tight leading-tight">Check your email</h1>
                <p className="text-muted-foreground mt-1.5 text-[15px]">
                  We sent a 4-digit code to <span className="font-medium text-foreground">{email}</span>.
                </p>
              </div>

              <div className="flex flex-col gap-5">
                <OtpInput value={otp} onChange={(v) => { setOtp(v); setError(""); }} disabled={loading} />

                {error && <p className="text-red-500 text-xs text-center">{error}</p>}

                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.replace(/\s/g, "").length < 4}
                  className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : "Verify code"}
                </button>

                <div className="text-center text-sm text-muted-foreground">
                  Didn&apos;t receive it?{" "}
                  <button
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                    className="text-primary font-medium hover:underline disabled:opacity-50"
                  >
                    {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
              </div>

              <button
                onClick={() => { setStep("email"); setOtp(""); setError(""); }}
                className="text-center text-sm text-muted-foreground flex items-center justify-center gap-1.5 hover:text-foreground transition-colors"
              >
                <ArrowLeft size={13} /> Change email
              </button>
            </motion.div>
          )}

          {/* ── Step: New Password ── */}
          {step === "password" && (
            <motion.div key="password" variants={panelVariants} initial="initial" animate="animate" exit="exit"
              transition={{ duration: 0.25 }} className="flex flex-col gap-6">
              <div>
                <h1 className="text-[28px] font-semibold tracking-tight leading-tight">New password</h1>
                <p className="text-muted-foreground mt-1.5 text-[15px]">
                  Choose a strong password for your account.
                </p>
              </div>

              <div className="flex flex-col gap-3.5">
                {/* Password */}
                <div className={`relative flex items-center border rounded-xl bg-background transition-colors ${
                  error ? "border-red-400" : "border-border focus-within:border-primary"
                }`}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="w-full px-4 py-3 text-sm bg-transparent outline-none pr-10"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Confirm */}
                <div className={`relative flex items-center border rounded-xl bg-background transition-colors ${
                  error ? "border-red-400" : "border-border focus-within:border-primary"
                }`}>
                  <input
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm password"
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                    className="w-full px-4 py-3 text-sm bg-transparent outline-none pr-10"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <ul className="text-[11px] text-muted-foreground space-y-1 pl-1">
                  <li className={password.length >= 8 ? "text-primary" : ""}>
                    {password.length >= 8 ? "✓" : "·"} At least 8 characters
                  </li>
                  <li className={password === confirm && confirm.length > 0 ? "text-primary" : ""}>
                    {password === confirm && confirm.length > 0 ? "✓" : "·"} Passwords match
                  </li>
                </ul>

                {error && <p className="text-red-500 text-xs">{error}</p>}

                <button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="w-full py-3 mt-1 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Reset password"}
                </button>
              </div>
            </motion.div>
          )}

          {/* ── Step: Done ── */}
          {step === "done" && (
            <motion.div key="done" variants={panelVariants} initial="initial" animate="animate" exit="exit"
              transition={{ duration: 0.25 }} className="flex flex-col items-center gap-6 text-center py-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-primary" />
              </div>
              <div>
                <h2 className="text-[24px] font-semibold">Password updated</h2>
                <p className="text-muted-foreground mt-2 text-[15px]">
                  Your password has been reset successfully. You can now sign in.
                </p>
              </div>
              <button
                onClick={() => router.push("/login")}
                className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                Back to sign in
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
